import React, { useRef, useState, useEffect, useMemo } from 'react';
import { 
  getWeekDays, 
  getScheduleForDate, 
  TIME_SLOTS 
} from '../../utils/calendarUtils';
import { Sun, SunMedium, Moon, Syringe, Flame, Pill, ChevronLeft, ChevronRight } from 'lucide-react';
import { normalizeCompoundCategory, formatDosageRange } from '../../utils/sortUtils';

export default function WeekView({
  currentDate,
  protocols = [],
  statusFilter = 'Active',
  categoryFilter = 'All',
  searchQuery = '',
  weekStart = 'sun',
  onSelectDay
}) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const weekDays = useMemo(() => {
    return getWeekDays(currentDate, weekStart);
  }, [currentDate, weekStart]);

  // Compute schedules for each day of the week
  const weekSchedules = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    const searchableProtocols = searchLower
      ? protocols.filter(p => 
          (p.name || '').toLowerCase().includes(searchLower) ||
          (p.intendedOutcome || '').toLowerCase().includes(searchLower) ||
          (p.notes || '').toLowerCase().includes(searchLower)
        )
      : protocols;

    return weekDays.map(day => {
      const schedule = getScheduleForDate(searchableProtocols, day.date, {
        statusFilter,
        categoryFilter
      });
      return {
        ...day,
        schedule
      };
    });
  }, [weekDays, protocols, statusFilter, categoryFilter, searchQuery]);

  const hasToday = weekSchedules.some(day => day.isToday);

  // Update edge scroll indicators
  const checkScrollEdges = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  // Auto-scroll to center Today's column on mount or date change
  const scrollToToday = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const todayEl = container.querySelector('[data-is-today="true"]');
    if (todayEl) {
      const targetScroll = todayEl.offsetLeft - (container.clientWidth / 2) + (todayEl.clientWidth / 2);
      container.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScrollEdges();
    // Delay slightly to ensure DOM layout is calculated
    const timer = setTimeout(() => {
      scrollToToday();
      checkScrollEdges();
    }, 150);

    return () => clearTimeout(timer);
  }, [currentDate, weekStart]);

  const getSlotIcon = (slotId) => {
    switch (slotId) {
      case 'AM': return Sun;
      case 'NOON': return SunMedium;
      case 'BEDTIME': return Moon;
      default: return Sun;
    }
  };

  const getCategoryTheme = (category) => {
    const cat = normalizeCompoundCategory(category);
    switch (cat) {
      case 'Peptide':
        return {
          border: 'border-purple-500/30',
          badge: 'bg-purple-500/15 text-purple-300',
          dot: 'bg-purple-400',
          icon: Syringe
        };
      case 'Hormones & AAS':
        return {
          border: 'border-rose-500/30',
          badge: 'bg-rose-500/15 text-rose-300',
          dot: 'bg-rose-400',
          icon: Flame
        };
      case 'Supplement':
      default:
        return {
          border: 'border-sky-500/30',
          badge: 'bg-sky-500/15 text-sky-300',
          dot: 'bg-sky-400',
          icon: Pill
        };
    }
  };

  return (
    <div className="space-y-3">
      {/* Mobile Swipe / Scrolling Affordance Bar */}
      <div className="flex sm:hidden items-center justify-between px-1 text-xs text-slate-400">
        <span className="flex items-center space-x-1.5 text-[11px] text-slate-400 bg-slate-900/90 px-3 py-1 rounded-full border border-slate-800 shadow-sm">
          <span>↔</span>
          <span>Swipe horizontally to view full week</span>
        </span>
        {hasToday && (
          <button
            type="button"
            onClick={scrollToToday}
            className="text-[11px] font-semibold text-sky-400 bg-sky-500/15 hover:bg-sky-500/25 px-2.5 py-1 rounded-full border border-sky-500/30 transition shadow-sm"
          >
            Center Today
          </button>
        )}
      </div>

      {/* Week Grid Container with Edge Fade Masks */}
      <div className="relative">
        {/* Left gradient fade mask */}
        {canScrollLeft && (
          <div className="pointer-events-none absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-[#0b1120] to-transparent z-10 sm:hidden" />
        )}

        {/* Right gradient fade mask */}
        {canScrollRight && (
          <div className="pointer-events-none absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-[#0b1120] to-transparent z-10 sm:hidden" />
        )}

        <div 
          ref={scrollContainerRef}
          onScroll={checkScrollEdges}
          className="overflow-x-auto pb-4 scroll-smooth"
        >
          <div className="min-w-[840px] space-y-4">
            
            {/* Week Columns Header */}
            <div className="grid grid-cols-7 gap-3">
              {weekSchedules.map(day => (
                <div
                  key={day.dateString}
                  data-is-today={day.isToday}
                  onClick={() => onSelectDay(day.date)}
                  className={`p-3 rounded-2xl border text-center transition cursor-pointer group ${
                    day.isToday
                      ? 'bg-slate-900 border-sky-500 shadow-md shadow-sky-500/10 ring-1 ring-sky-500/30'
                      : 'bg-slate-900/50 border-slate-800 hover:bg-slate-800/60 hover:border-slate-700'
                  }`}
                >
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider group-hover:text-slate-300">
                    {day.shortName}
                  </div>
                  <div className="mt-1 flex items-center justify-center space-x-1.5">
                    <span
                      className={`text-lg font-extrabold w-8 h-8 flex items-center justify-center rounded-xl ${
                        day.isToday
                          ? 'bg-sky-500 text-white shadow'
                          : 'text-white'
                      }`}
                    >
                      {day.dayNumber}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] font-semibold text-slate-400">
                    {day.schedule.totalScheduled} {day.schedule.totalScheduled === 1 ? 'dose' : 'doses'}
                  </div>
                </div>
              ))}
            </div>

            {/* 3 Time Slots Rows (AM, Noon, Bedtime) */}
            {TIME_SLOTS.map(slot => {
              const SlotIcon = getSlotIcon(slot.id);

              return (
                <div key={slot.id} className="space-y-2">
                  {/* Slot Header */}
                  <div className="flex items-center space-x-2 px-2">
                    <span className={`p-1 rounded-md ${slot.bgColor} ${slot.textColor} border ${slot.borderColor}`}>
                      <SlotIcon className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-xs font-bold text-slate-200">
                      {slot.label}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      • {slot.timeRange}
                    </span>
                  </div>

                  {/* 7 Days for this slot */}
                  <div className="grid grid-cols-7 gap-3">
                    {weekSchedules.map(day => {
                      const slotProtocols = day.schedule.bySlot[slot.id] || [];

                      return (
                        <div
                          key={`${day.dateString}-${slot.id}`}
                          onClick={() => onSelectDay(day.date)}
                          className={`min-h-[140px] p-2.5 rounded-2xl border transition cursor-pointer flex flex-col space-y-2 ${
                            day.isToday
                              ? 'bg-slate-900/80 border-slate-700/80 hover:border-sky-500/50'
                              : 'bg-slate-900/30 border-slate-800/80 hover:bg-slate-900/60 hover:border-slate-700'
                          }`}
                        >
                          {slotProtocols.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-[11px] text-slate-600 italic">
                              None
                            </div>
                          ) : (
                            slotProtocols.map((protocol, idx) => {
                              const theme = getCategoryTheme(protocol.category);
                              const CategoryIcon = theme.icon;
                              const dosageDisplay = formatDosageRange(protocol);

                              return (
                                <div
                                  key={`${protocol.id || protocol.name}-${idx}`}
                                  className={`p-2 rounded-xl bg-slate-950/80 border ${theme.border} hover:border-slate-500 transition space-y-1`}
                                >
                                  <div className="flex items-center justify-between gap-1">
                                    <span className="font-bold text-xs text-white truncate" title={protocol.name}>
                                      {protocol.name}
                                    </span>
                                    <span className={`p-0.5 rounded ${theme.badge} shrink-0`} title={normalizeCompoundCategory(protocol.category)}>
                                      <CategoryIcon className="w-2.5 h-2.5" />
                                    </span>
                                  </div>

                                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                                    <span className="font-bold text-sky-300">
                                      {dosageDisplay}
                                    </span>
                                    {protocol.administrationFormat && (
                                      <span className="truncate max-w-[55px] text-slate-500">
                                        {protocol.administrationFormat}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </div>
    </div>
  );
}
